import { PrincipalIdSchema } from "@core/domains/identity";
import { isoDate } from "@core/foundation/date";
import { identity } from "@core/foundation/function";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { ProjectIdSchema } from "./project.ts";
import { ID_PREFIX } from "./shared/id-prefix.ts";

const { generateId: generateAgentSessionId, idSchema: AgentSessionIdSchema } =
  createModelId({ idPrefix: ID_PREFIX.AGENT_SESSION });

export const AgentSession = createModel({
  create: (item, schema) => {
    const now = isoDate();

    return {
      ...schema.decode(item),
      id: generateAgentSessionId(),
      lastPromptAt: now,
      startedAt: now,
    };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        id: AgentSessionIdSchema,
        lastPromptAt: z.iso.datetime(),
        principalId: PrincipalIdSchema,
        projectId: ProjectIdSchema,
        sessionId: z.safeId(),
        startedAt: z.iso.datetime(),
      },
      {
        id: "The unique identifier for the session record.",
        lastPromptAt: "When the service received the session's latest prompt.",
        principalId: "The id of the principal the session belongs to.",
        projectId: "The id of the project the session belongs to.",
        sessionId: "The id the agent host gave the session.",
        startedAt: "When the service received the session's first prompt.",
      },
    ),
    derive: {
      create: schema =>
        schema.omit({ id: true, lastPromptAt: true, startedAt: true }),
      recovery: identity,
      update: schema => schema.pick({ lastPromptAt: true }).partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface AgentSession extends z.infer<typeof AgentSession.schema> {}
